/* Shared helpers for CRM stage tagging (Not Contacted / Current /
 * Positive / Negative). Used by both CRMReporting.jsx and CRMClients.jsx.
 */
import { useState } from "react";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ThumbsUp, ThumbsDown, X as XIcon } from "lucide-react";

export const STAGE_KEYS = ["Not Contacted", "Current", "Positive", "Negative"];

export const STAGE_STYLES = {
  "Not Contacted": "bg-slate-100 text-slate-600 border-slate-200",
  "Current":      "bg-blue-100 text-blue-700 border-blue-200",
  "Positive":     "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Negative":     "bg-rose-100 text-rose-700 border-rose-200",
};

export const STAGE_CHIP_ACTIVE = {
  "Not Contacted": "bg-slate-600 border-slate-600 text-white",
  "Current":      "bg-blue-600 border-blue-600 text-white",
  "Positive":     "bg-emerald-600 border-emerald-600 text-white",
  "Negative":     "bg-rose-600 border-rose-600 text-white",
};

export function deriveStage(c) {
  if (c?.crm_tag === "Positive" || c?.crm_tag === "Negative") return c.crm_tag;
  if ((c?.log_count || 0) > 0) return "Current";
  return "Not Contacted";
}

export function CrmStageBadge({ client, onCleared, isMasterAdmin = false, currentUserId = "", busy = false, setBusy }) {
  const stage = client?.crm_stage || deriveStage(client);
  const isManualTag = stage === "Positive" || stage === "Negative";
  const tone = STAGE_STYLES[stage] || STAGE_STYLES["Not Contacted"];
  // The assignee can undo their own Negative tag within the 3-day window.
  const deadline = client?.crm_tag_unassign_at ? new Date(client.crm_tag_unassign_at) : null;
  const withinGrace = !!(deadline && deadline.getTime() > Date.now());
  const isOwner = currentUserId && client.assigned_to_id === currentUserId;
  const canOwnerClear = stage === "Negative" && isOwner && withinGrace;
  const canClear = isMasterAdmin || canOwnerClear;
  // Hours remaining in the 3-day grace window for Negative tags.
  let graceLabel = "";
  if (stage === "Negative" && deadline) {
    const hrs = Math.max(0, Math.round((deadline.getTime() - Date.now()) / 36e5));
    graceLabel = hrs >= 24 ? `${Math.floor(hrs / 24)}d ${hrs % 24}h` : `${hrs}h`;
  }
  const clearTag = async () => {
    if (busy) return;
    if (!window.confirm(`Remove the ${stage} tag from ${client.client_name}?`)) return;
    setBusy?.(true);
    try {
      await api.delete(`/crm/clients/${client.id}/tag`);
      toast.success("Tag removed");
      onCleared?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy?.(false); }
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${tone}`}
      title={
        client.crm_tag_by_name
          ? `${stage} — set by ${client.crm_tag_by_name}${client.crm_tag_at ? ` on ${new Date(client.crm_tag_at).toLocaleDateString()}` : ""}${stage === "Negative" && deadline ? ` · auto-unassign in ${graceLabel}` : ""}`
          : stage
      }
      data-testid={`crm-stage-badge-${client.id}`}
    >
      {stage}
      {stage === "Negative" && deadline && withinGrace && (
        <span className="font-mono text-[9px] opacity-80" data-testid={`crm-stage-countdown-${client.id}`}>· {graceLabel}</span>
      )}
      {stage === "Negative" && deadline && !withinGrace && !client.assigned_to_id && (
        <span className="font-mono text-[9px] opacity-80">· unassigned</span>
      )}
      {isManualTag && canClear && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); clearTag(); }}
          className="hover:bg-black/10 rounded-full ml-0.5 -mr-0.5 p-0.5"
          title={isMasterAdmin ? "Remove tag (Master Admin)" : "Undo Negative (within 3-day window)"}
          data-testid={`crm-stage-clear-${client.id}`}
        >
          <XIcon size={10} strokeWidth={3} />
        </button>
      )}
    </span>
  );
}

export function CrmStageButtons({ client, onChanged }) {
  const [busy, setBusy] = useState(false);
  const tagAs = async (tag) => {
    if (busy) return;
    if (client.crm_tag === tag) { toast.info(`Already marked ${tag}`); return; }
    setBusy(true);
    try {
      await api.post(`/crm/clients/${client.id}/tag`, { tag });
      toast.success(`Marked ${tag}`);
      onChanged?.(tag);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const isPos = client.crm_tag === "Positive";
  const isNeg = client.crm_tag === "Negative";
  return (
    <div className="flex items-center gap-1 shrink-0" data-testid={`crm-stage-buttons-${client.id}`}>
      <Button
        size="sm" variant="outline" disabled={busy}
        onClick={() => tagAs("Positive")}
        className={`h-7 px-2 text-[10.5px] ${isPos ? "bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 ring-2 ring-emerald-300 shadow" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}
        title={isPos ? "Currently marked Positive" : "Mark as Positive lead"}
        data-testid={`crm-stage-positive-${client.id}`}
      >
        <ThumbsUp size={11} className="mr-0.5" /> Pos
      </Button>
      <Button
        size="sm" variant="outline" disabled={busy}
        onClick={() => tagAs("Negative")}
        className={`h-7 px-2 text-[10.5px] ${isNeg ? "bg-rose-600 border-rose-600 text-white hover:bg-rose-700 ring-2 ring-rose-300 shadow" : "border-rose-200 text-rose-700 hover:bg-rose-50"}`}
        title={isNeg ? "Currently marked Negative" : "Mark as Negative lead"}
        data-testid={`crm-stage-negative-${client.id}`}
      >
        <ThumbsDown size={11} className="mr-0.5" /> Neg
      </Button>
    </div>
  );
}

export function CrmStageFilterChips({ value, onChange, counts, testidPrefix = "crm-stage-chip", inline = false }) {
  const STAGE_ICONS = { "Not Contacted": "○", "Current": "◐", "Positive": "▲", "Negative": "▼" };
  const Wrapper = inline ? "span" : "div";
  return (
    <Wrapper className={inline ? "inline-flex items-center gap-1 flex-wrap" : "flex items-center gap-1 flex-wrap"} data-testid={`${testidPrefix}-strip`}>
      {!inline && <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-slate-500 mr-1">Stage</span>}
      <button
        onClick={() => onChange("all")}
        className={`h-7 px-2.5 text-[10.5px] font-bold rounded-full border inline-flex items-center gap-1 transition-all ${
          value === "all"
            ? "bg-gradient-to-br from-sky-500 via-blue-500 to-cyan-600 border-transparent text-white shadow-sm shadow-blue-500/40 ring-1 ring-blue-300 -translate-y-[1px]"
            : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 hover:-translate-y-[1px] hover:shadow-sm"
        }`}
        data-testid={`${testidPrefix}-all`}
      >
        {value === "all" && <span className="w-1 h-1 rounded-full bg-white/95" />}
        <span className="text-[9px] opacity-90">◆</span>
        All <span className="mono opacity-90">({counts?.total ?? 0})</span>
      </button>
      {STAGE_KEYS.map((k) => (
        <button
          key={k}
          onClick={() => onChange(k)}
          className={`h-7 px-2.5 text-[10.5px] font-bold rounded-full border inline-flex items-center gap-1 transition-all ${
            value === k
              ? `${STAGE_CHIP_ACTIVE[k]} border-transparent shadow-sm ring-1 -translate-y-[1px]`
              : `bg-white ${STAGE_STYLES[k]} hover:opacity-80 hover:-translate-y-[1px] hover:shadow-sm`
          }`}
          data-testid={`${testidPrefix}-${k.replace(/\s+/g, "_")}`}
        >
          <span className="text-[9px] opacity-80">{STAGE_ICONS[k]}</span>
          {k} <span className="mono opacity-80">({counts?.[k] ?? 0})</span>
        </button>
      ))}
    </Wrapper>
  );
}

/** Used by CRMClients.jsx to show "View Logs" history with full details. */
export function CrmStageLogsDialog({ open, client, onOpenChange }) { // eslint-disable-line no-unused-vars
  // Lightweight wrapper — implementation lives in CRMClients to avoid
  // circular dependencies on lead status styles. Kept here as a placeholder
  // export for future shared use.
  return null;
}
